import * as React from 'react'
import { Copy, Download, ExternalLink, Eye, FileJson, Info, RefreshCw } from 'lucide-react'
import {
  useDomain, useDomainEvents, useDomainScore, useCreateWatcher,
  useRegistrarOffer, useResolveDomain,
} from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { api, ApiError } from '@/lib/api'
import type { Observation, RawObservation } from '@/lib/types'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DrawerContent,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger, Separator } from '@/components/ui/misc'
import { HistoryPanel } from '@/features/domain/HistoryPanel'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, ErrorState, LoadingState, StaleState } from '@/components/ui/states'
import {
  FeatureTags, GfwBadge, StatusBadge, TierBadge, sourceLabel,
} from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { cn, formatDateTime, formatUTC, formatNumber } from '@/lib/utils'

/**
 * Domain detail drawer (§5.5): overview, lifecycle timeline, evidence and the
 * caller's own relationships. It never shows another user's rules or channels.
 */
export function DomainDetailDrawer({
  domainId,
  onClose,
}: {
  domainId: string | null
  onClose: () => void
}) {
  const { user } = useAuth()
  const toast = useToast()
  const { data, isLoading, error, refetch } = useDomain(domainId)
  const events = useDomainEvents(domainId)
  const score = useDomainScore(domainId)
  const resolve = useResolveDomain()
  const createWatcher = useCreateWatcher()
	const [rawResponse, setRawResponse] = React.useState<RawObservation | null>(null)
	const [rawLoading, setRawLoading] = React.useState('')

  const d = data?.domain

	async function openRawResponse(observation: Observation, source: string) {
		if (!domainId) return
		const key = `${observation.id}:${source}`
		setRawLoading(key)
		try {
			const result = await api.get<RawObservation>(
				`/domains/${domainId}/observations/${observation.id}/raw/${encodeURIComponent(source)}`,
			)
			setRawResponse(result)
		} catch (err) {
			toast.error('读取原始响应失败', err instanceof ApiError ? err.message : undefined)
		} finally {
			setRawLoading('')
		}
	}

	return (
		<>
		<Dialog open={Boolean(domainId)} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent aria-describedby="domain-detail-desc">
        <div className="border-b p-5">
          <DialogTitle className="truncate pr-8 font-mono text-base">
            {d?.domain_unicode && d.domain_unicode !== d.domain_ascii
              ? `${d.domain_unicode} (${d.domain_ascii})`
              : d?.domain_ascii ?? '域名详情'}
          </DialogTitle>
          <DialogDescription id="domain-detail-desc" className="mt-1">
            {data?.disclaimer ?? '共享域名主数据'}
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <LoadingState rows={5} label="正在加载域名详情" />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !d ? null : (
            <>
              {!data.freshness.fresh && data.freshness.last_resolved_at && (
                <StaleState className="mb-4" confirmedAt={formatUTC(data.freshness.last_resolved_at)} />
              )}
              {!data.freshness.last_resolved_at && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-muted-foreground/30 bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>该域名尚未经过外部确认。Zone 中缺失或未观察到都不等于可注册。</span>
                </div>
              )}

              <div className="mb-4 flex flex-wrap items-center gap-2">
                <StatusBadge
                  status={d.current_status}
                  source={d.status_source}
                  confirmedAt={d.last_resolved_at}
                  confidence={d.status_confidence}
                />
                <GfwBadge status={d.gfw_risk_status} />
                <TierBadge tier={d.monitor_tier} />
                {d.watched && <Badge variant="success">监控中</Badge>}
              </div>

              <div className="mb-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={resolve.isPending}
                  onClick={async () => {
                    try {
                      const res = await resolve.mutateAsync(d.id)
                      toast.success('已排入确认队列', res.message)
                    } catch (err) {
                      toast.error(
                        '无法立即确认',
                        err instanceof ApiError ? err.message : undefined,
                      )
                    }
                  }}
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', resolve.isPending && 'animate-spin')} aria-hidden />
                  立即确认
                </Button>
                {!d.watched && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={createWatcher.isPending}
                    onClick={async () => {
                      try {
                        const res = await createWatcher.mutateAsync({ domain_id: d.id })
                        toast.success('已加入监控', res.notice || undefined)
                      } catch (err) {
                        toast.error('加入监控失败', err instanceof ApiError ? err.message : undefined)
                      }
                    }}
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden /> 加入监控
                  </Button>
                )}
              </div>

              <Tabs defaultValue="overview">
                <TabsList>
                  <TabsTrigger value="overview">概览</TabsTrigger>
                  <TabsTrigger value="lifecycle">生命周期</TabsTrigger>
                  <TabsTrigger value="evidence">证据</TabsTrigger>
                  <TabsTrigger value="value">价值</TabsTrigger>
                  <TabsTrigger value="history">历史</TabsTrigger>
                  <TabsTrigger value="action">去注册</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <SectionTitle>域名信息</SectionTitle>
                    <Field label="规范化域名" value={<code className="text-xs">{d.domain_ascii}</code>} />
                    <Field label="可注册域名" value={<code className="text-xs">{d.registrable_domain}</code>} />
                    <Field label="主 label" value={d.sld} />
                    <Field label="后缀" value={`.${d.tld}`} />
                    <Field label="label 长度" value={d.label_length} />
                    <Field label="完整长度" value={d.total_length} />
                    <Field label="字符集" value={d.charset_class} />
                    <Field label="注册时间" value={formatDateTime(d.registered_at, user?.timezone)} />
                    <Field label="注册记录最后变更" value={formatDateTime(d.registration_updated_at, user?.timezone)} />
                    <Field label="到期时间" value={formatDateTime(d.expires_at, user?.timezone)} />
                    <Field
                      label="预计释放"
                      value={
                        d.drop_estimate_at ? (
                          <Tooltip content={`来源：${d.drop_estimate_source ?? '未知'}。估算值，不构成释放时刻承诺。`}>
                            <span>{formatDateTime(d.drop_estimate_at, user?.timezone)}</span>
                          </Tooltip>
                        ) : '未估算'
                      }
                    />
                    <div className="col-span-2">
                      <dt className="mb-1 text-xs text-muted-foreground">域名状态</dt>
                      <dd className="flex flex-wrap gap-1">
                        {(data.observations?.[0]?.raw_statuses?.length ?? 0) > 0
                          ? data.observations[0].raw_statuses!.map((status) => (
                              <Badge key={status} variant="outline" className="font-normal">{status}</Badge>
                            ))
                          : <span className="text-muted-foreground">暂无原始状态</span>}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="mb-1 text-xs text-muted-foreground">名称服务器</dt>
                      <dd className="flex flex-wrap gap-1">
                        {d.nameservers?.length
                          ? d.nameservers.map((ns) => <Badge key={ns} variant="secondary" className="font-mono font-normal">{ns}</Badge>)
                          : <span className="text-muted-foreground">未知</span>}
                      </dd>
                    </div>

                    <SectionTitle>注册商信息</SectionTitle>
                    <Field label="注册商" value={d.current_registrar ?? '未知'} />
                    <Field label="IANA ID" value={d.registrar_iana_id ?? '未知'} />
                    <Field
                      label="滥用联系邮箱"
                      value={d.registrar_abuse_email
                        ? <a className="text-primary hover:underline" href={`mailto:${d.registrar_abuse_email}`}>{d.registrar_abuse_email}</a>
                        : '未知'}
                    />
                    <Field label="滥用联系电话" value={d.registrar_abuse_phone ?? '未知'} />

                    <SectionTitle>平台记录</SectionTitle>
                    <Field label="首次发现" value={formatDateTime(d.first_seen_at, user?.timezone)} />
                    <Field label="最后观察" value={formatDateTime(d.last_seen_at, user?.timezone)} />
                    <Field
                      label="注册次数"
                      value={
                        // §7.6: unknown history is not zero, and the UI must
                        // not let the two look the same.
                        d.registration_count == null
                          ? <span className="text-muted-foreground">未知（无平台历史）</span>
                          : formatNumber(d.registration_count)
                      }
                    />
                    <Field
                      label="Drop 次数"
                      value={d.drop_count == null ? <span className="text-muted-foreground">未知</span> : formatNumber(d.drop_count)}
                    />
                    <div className="col-span-2">
                      <dt className="mb-1 text-xs text-muted-foreground">特征</dt>
                      <dd><FeatureTags tags={d.feature_tags} max={12} /></dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="mb-1 text-xs text-muted-foreground">数据来源</dt>
                      <dd className="flex flex-wrap gap-1">
                        {d.sources?.map((s) => (
                          <Badge key={s} variant="outline" className="font-normal">{sourceLabel(s)}</Badge>
                        ))}
                      </dd>
                    </div>
                    {d.matched_rules?.length ? (
                      <div className="col-span-2">
                        <dt className="mb-1 text-xs text-muted-foreground">命中我的规则</dt>
                        <dd className="flex flex-wrap gap-1">
                          {d.matched_rules.map((r) => (
                            <Badge key={r} variant="secondary">{r}</Badge>
                          ))}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                </TabsContent>

                <TabsContent value="lifecycle">
                  {events.isLoading ? (
                    <LoadingState rows={4} />
                  ) : (events.data?.items.length ?? 0) === 0 ? (
                    <EmptyState title="暂无生命周期事件" description="状态发生有效变化时会在此记录。" />
                  ) : (
                    <ol className="space-y-3">
                      {events.data!.items.map((e, index, items) => {
                        const previousStatusEvent = e.event_type === 'status_changed'
                          ? items.slice(index + 1).find((item) => item.event_type === 'status_changed')
                          : undefined
                        return (
                        <li key={e.id} className="relative border-l pl-4">
                          <span className="absolute -left-1 top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden />
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{eventLabel(e.event_type)}</Badge>
                            {e.from_status && e.to_status && (
                              <span className="text-xs text-muted-foreground">
                                {e.from_status} → {e.to_status}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatDateTime(e.observed_at, user?.timezone)} · 来源 {sourceLabel(e.source)} · 置信度 {e.confidence}
                            {previousStatusEvent && <> · 距上次状态变化 {formatElapsed(previousStatusEvent.observed_at, e.observed_at)}</>}
                          </p>
                          {typeof e.metadata?.note === 'string' && (
                            <p className="mt-1 text-xs text-muted-foreground">{e.metadata.note}</p>
                          )}
                        </li>
                        )
                      })}
                    </ol>
                  )}
                </TabsContent>

                <TabsContent value="evidence">
                  {(data.observations?.length ?? 0) === 0 ? (
                    <EmptyState title="暂无观察记录" description="外部确认完成后，这里会显示每次查询的事实。" />
                  ) : (
                    <div className="space-y-3">
                      {data.observations.map((o, i) => (
                        <div key={i} className="rounded-md border p-3 text-xs">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium">{sourceLabel(o.source)}</span>
                            <span className="text-muted-foreground">{formatUTC(o.queried_at)}</span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-muted-foreground">
                            <span>规范化状态：{o.normalized_status}</span>
                            <span>置信度：{o.confidence}</span>
                            {o.http_status ? <span>HTTP：{o.http_status}</span> : null}
                            {o.latency_ms ? <span>延迟：{o.latency_ms} ms</span> : null}
                            {o.registrar ? <span className="col-span-2">注册商：{o.registrar}</span> : null}
                            {o.error_class ? (
                              <span className="col-span-2 text-destructive">错误类别：{o.error_class}</span>
                            ) : null}
                          </div>
                          {o.raw_statuses?.length ? (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {o.raw_statuses.map((s) => (
                                <Badge key={s} variant="outline" className="font-normal">{s}</Badge>
                              ))}
                            </div>
                          ) : null}
                          {o.events && Object.keys(o.events).length > 0 && (
                            <div className="mt-2 rounded bg-muted/50 p-2 text-muted-foreground">
                              {Object.entries(o.events).map(([event, at]) => (
                                <p key={event}>{registrationEventLabel(event)}：{formatUTC(at)}</p>
                              ))}
                            </div>
                          )}
                          {o.nameservers?.length ? (
                            <p className="mt-2 break-all text-muted-foreground">名称服务器：{o.nameservers.join('、')}</p>
                          ) : null}
                          {o.details?.additional_sources?.length ? (
                            <p className="mt-2 text-muted-foreground">
                              补充来源：{o.details.additional_sources.map(sourceLabel).join('、')}
                            </p>
                          ) : null}
						  {o.raw_sources?.length ? (
							<div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
							  <span className="text-muted-foreground">原始响应</span>
							  {o.raw_sources.map((source) => {
								const loading = rawLoading === `${o.id}:${source}`
								return (
								  <Button
									key={source}
									type="button"
									variant="outline"
									className="h-7 px-2 text-xs"
									disabled={Boolean(rawLoading)}
									onClick={() => void openRawResponse(o, source)}
								  >
									<FileJson className="h-3.5 w-3.5" aria-hidden />
									{loading ? '读取中…' : `查看 ${source.toUpperCase()} 原文`}
								  </Button>
								)
							  })}
							</div>
						  ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="value">
                  {score.isLoading ? (
                    <LoadingState rows={3} />
                  ) : !score.data ? (
                    <EmptyState title="尚未计算价值分" description="域名入库后会异步计算，缺失信号不按 0 处理。" />
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-baseline gap-3">
                        <span className="text-3xl font-semibold tabular">{score.data.score.toFixed(1)}</span>
                        <div className="text-xs text-muted-foreground">
                          <p>模型版本 {score.data.score_version}</p>
                          <p>置信度 {score.data.confidence}/100</p>
                        </div>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        {score.data.components.map((c) => (
                          <div key={c.key} className="rounded-md border p-2.5 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{componentLabel(c.key)}</span>
                              {c.available ? (
                                <span className="tabular">
                                  {c.value.toFixed(1)} × {c.weight.toFixed(2)} = {c.contribution.toFixed(1)}
                                </span>
                              ) : (
                                <Badge variant="muted">信号缺失</Badge>
                              )}
                            </div>
                            {c.evidence?.length ? (
                              <p className="mt-1 text-muted-foreground">{c.evidence.join(' · ')}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">{score.data.disclaimer}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="history">
                  <HistoryPanel domainId={d.id} />
                </TabsContent>

                <TabsContent value="action">
                  <RegistrarOffer domainId={d.id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </DrawerContent>
		</Dialog>

		<RawResponseDialog
			response={rawResponse}
			onClose={() => setRawResponse(null)}
			onCopied={() => toast.success('原始响应已复制')}
			onCopyFailed={() => toast.error('复制失败', '请使用下载原文，或在原文区域手动复制')}
		/>
		</>
  )
}

function formatElapsed(from: string, to: string): string {
  const seconds = Math.max(0, (Date.parse(to) - Date.parse(from)) / 1000)
  const days = seconds / 86400
  if (days >= 1) return `${days.toLocaleString('zh-CN', { maximumFractionDigits: 1 })} 天`
  const hours = seconds / 3600
  if (hours >= 1) return `${hours.toLocaleString('zh-CN', { maximumFractionDigits: 1 })} 小时`
  return `${Math.max(0, Math.round(seconds / 60))} 分钟`
}

function RawResponseDialog({
	response,
	onClose,
	onCopied,
	onCopyFailed,
}: {
	response: RawObservation | null
	onClose: () => void
	onCopied: () => void
	onCopyFailed: () => void
}) {
	const formatted = React.useMemo(() => {
		if (!response) return ''
		if (response.content_type !== 'application/json') return response.body
		try {
			return JSON.stringify(JSON.parse(response.body), null, 2)
		} catch {
			return response.body
		}
	}, [response])

	function downloadResponse() {
		if (!response) return
		const blob = new Blob([response.body], { type: `${response.content_type};charset=utf-8` })
		const url = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = response.filename
		document.body.appendChild(link)
		link.click()
		link.remove()
		URL.revokeObjectURL(url)
	}

	async function copyResponse() {
		if (!formatted) return
		let copied = false
		if (navigator.clipboard?.writeText) {
			try {
				await Promise.race([
					navigator.clipboard.writeText(formatted),
					new Promise<never>((_, reject) => {
						window.setTimeout(() => reject(new Error('clipboard timeout')), 500)
					}),
				])
				copied = true
			} catch {
				// Fall through to execCommand for browsers without clipboard permission.
			}
		}
		if (!copied) {
			const input = document.createElement('textarea')
			input.value = formatted
			input.setAttribute('readonly', '')
			input.style.position = 'fixed'
			input.style.opacity = '0'
			document.body.appendChild(input)
			input.select()
			copied = document.execCommand('copy')
			input.remove()
		}
		if (copied) onCopied()
		else onCopyFailed()
	}

	return (
		<Dialog open={Boolean(response)} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle>原始 {response?.source.toUpperCase()} 响应</DialogTitle>
					<DialogDescription>
						{response?.domain_ascii} · 查询于 {formatDateTime(response?.queried_at)}（北京时间） · {formatNumber(response?.size_bytes)} 字节
					</DialogDescription>
				</DialogHeader>
				<div className="rounded-md border bg-muted/30">
					<div className="border-b px-3 py-2 font-mono text-[11px] text-muted-foreground">
						SHA-256：<span className="break-all">{response?.sha256}</span>
					</div>
					<pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs leading-relaxed">
						{formatted}
					</pre>
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" onClick={downloadResponse}>
						<Download className="h-3.5 w-3.5" aria-hidden /> 下载原文
					</Button>
					<Button
						type="button"
						onClick={() => void copyResponse()}
					>
						<Copy className="h-3.5 w-3.5" aria-hidden /> 复制原文
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

/** Affiliate action panel (§18.6): disclosure, evidence time, no guarantee. */
function RegistrarOffer({ domainId }: { domainId: string }) {
  const { data, isLoading, error } = useRegistrarOffer(domainId)

  if (isLoading) return <LoadingState rows={2} />
  if (error) return <ErrorState error={error} />
  if (!data) return null

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
        <p className="font-medium text-amber-900 dark:text-amber-300">佣金披露</p>
        <p className="mt-1 text-amber-800 dark:text-amber-400">{data.disclosure}</p>
        <p className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-500">
          披露版本 {data.disclosure_version}
        </p>
      </div>

      <div className="rounded-md border p-3 text-xs">
        <p>
          点击时状态：<strong>{data.status_at_offer}</strong>
          {data.status_confirmed_at && <>（确认于 {formatUTC(data.status_confirmed_at)}）</>}
        </p>
        {data.status_warning && (
          <p className="mt-1 text-amber-700 dark:text-amber-400">{data.status_warning}</p>
        )}
        <p className="mt-1 text-muted-foreground">{data.guarantee}</p>
      </div>

      {data.options.length === 0 ? (
        <EmptyState
          title="暂无可用注册商"
          description="运营尚未启用任何联盟注册商，或该后缀不在合作范围内。"
        />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{data.ranking_explanation}</p>
          <div className="space-y-2">
            {data.options.map((o) => (
              <a
                key={o.registrar}
                href={o.action_url}
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                className="flex items-center justify-between rounded-md border p-3 text-sm transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div>
                  <p className="font-medium">{o.name}</p>
                  {o.commission_note && (
                    <p className="text-xs text-muted-foreground">{o.commission_note}</p>
                  )}
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden />
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{value ?? '—'}</dd>
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="col-span-2 border-b pb-1 pt-2 text-sm font-semibold first:pt-0">
      {children}
    </div>
  )
}

function registrationEventLabel(event: string) {
  const labels: Record<string, string> = {
    registration: '注册时间',
    expiration: '到期时间',
    'last changed': '注册记录最后变更',
    'last update of rdap database': 'RDAP 数据更新时间',
  }
  return labels[event] ?? event
}

function eventLabel(type: string): string {
  const map: Record<string, string> = {
    status_changed: '状态变化',
    date_changed: '日期更新',
    registrar_changed: '注册商变化',
    zone_added: 'Zone 新增观察',
    zone_removed: 'Zone 未观察到',
    registration_started: '进入新注册周期',
    availability_confirmed: '可注册确认',
    score_changed: '价值分变化',
    gfw_changed: 'GFW 信号变化',
  }
  return map[type] ?? type
}

function componentLabel(key: string): string {
  const map: Record<string, string> = {
    lexical: '词法与可记忆性',
    suffix_market: '后缀与市场',
    lifecycle: '生命周期可行动性',
    history: '历史与年龄',
    risk_penalty: '风险扣分',
  }
  return map[key] ?? key
}
